export interface ProjectContext {
  name: string;
  goal: string;
  description: string;
  industry: string;
  compliance: string[];
}

export interface TechStack {
  languages: string[];
  frameworks: string[];
  databases: string[];
  cloudProvider: string;
  infrastructure: string[];
  packageManager: string;
  monorepo: boolean;
  monorepoTool: string;
}

export interface TeamStructure {
  size: string;
  seniorityMix: {
    junior: number;
    mid: number;
    senior: number;
    lead: number;
  };
  claudeUsers: string[];
  dedicatedReviewers: boolean;
  timezones: string;
}

export interface GitWorkflow {
  branchingStrategy: string;
  branchNaming: string;
  commitStandard: string;
  signedCommits: boolean;
  prRequirements: string[];
  protectedBranches: string[];
}

export interface CodingStandards {
  linting: string;
  formatting: string;
  testRequirements: string[];
  coverageThreshold: number;
  architecture: string;
  sensitivePaths: string[];
  dependencyPolicy: string;
  maxFileLength: number;
  namingConventions: string[];
  customConventions: string;
}

export interface MCPConfig {
  ticketSystem: string;
  documentation: string[];
  communication: string[];
  sourceControl: string;
  cloudConsole: string;
  monitoring: string[];
  customServers: CustomMCPServer[];
}

export interface CustomMCPServer {
  name: string;
  command: string;
  args: string;
  description: string;
}

export interface CICD {
  platform: string;
  stages: string[];
  autoDeploy: string;
  aiCodeFlag: boolean;
  codeOwnership: boolean;
}

export interface WizardData {
  projectContext: ProjectContext;
  techStack: TechStack;
  teamStructure: TeamStructure;
  gitWorkflow: GitWorkflow;
  codingStandards: CodingStandards;
  mcpConfig: MCPConfig;
  cicd: CICD;
  plugins: string[];
}

export interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  tags: string[];
  stars: number;
  forks: number;
  author: string;
  createdAt: string;
  updatedAt: string;
  techStack: string[];
  wizardData: WizardData;
  latestVersion?: any;
  files?: GeneratedFile[];
}

export interface Generation {
  id: string;
  name: string;
  status: 'pending' | 'generating' | 'completed' | 'failed';
  createdAt: string;
  completedAt?: string;
  projectContext: ProjectContext;
  wizardData: WizardData;
  outputFiles?: Record<string, string>;
  files?: GeneratedFile[];
  error?: string;
}

export interface GeneratedFile {
  path: string;
  content: string;
  type: 'file' | 'directory';
  children?: GeneratedFile[];
}

export interface FileTreeNode {
  name: string;
  path: string;
  type: 'file' | 'directory';
  children?: FileTreeNode[];
}

// ---------------------------------------------------------------------------
// Playbook Types
// ---------------------------------------------------------------------------
export interface PromptTemplate {
  id: string;
  name: string;
  description: string;
  prompt: string;
  expectedOutput?: string;
  tips?: string[];
}

export interface PlaybookPhaseDeliverable {
  name: string;
  description?: string;
}

export interface PlaybookPhase {
  id: string;
  name: string;
  description: string;
  objectives: string[];
  deliverables: (string | PlaybookPhaseDeliverable)[];
  qualityGates: string[];
  claudeInstructions: string;
  claudeDo?: string[];
  claudeDoNot?: string[];
  promptTemplates: PromptTemplate[];
}

export interface Playbook {
  id: string;
  name: string;
  projectType: string;
  description: string;
  icon: string;
  phases: PlaybookPhase[];
}

/** Lightweight phase shape returned by the list endpoint (counts, not arrays) */
export interface PlaybookPhaseSummary {
  id: string;
  name: string;
  description: string;
  objectiveCount: number;
  deliverableCount: number;
  promptCount: number;
}

/** Lightweight playbook shape returned by GET /api/playbooks */
export interface PlaybookSummary {
  id: string;
  name: string;
  projectType: string;
  description: string;
  icon: string;
  phaseCount: number;
  phases: PlaybookPhaseSummary[];
}

export const WIZARD_STEPS = [
  'Project Context',
  'Tech Stack',
  'Team Structure',
  'Git Workflow',
  'Coding Standards',
  'MCP Config',
  'CI/CD',
  'Plugins',
  'Review',
] as const;

export type WizardStep = (typeof WIZARD_STEPS)[number];
